import { useState } from "react";
import { api } from "./api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useResource, SectionHeader, Loading, Modal, ConfirmDialog, Field, PrimaryBtn, GhostBtn, ImagePicker, inputCls } from "./components";

const EMPTY = { name: "", num: "", pos: "P", grade: 12, bt: "R/R", photo: "" };
const GRADES = { 9: "Freshman", 10: "Sophomore", 11: "Junior", 12: "Senior" };
const POSITIONS = ["P", "C", "1B", "2B", "3B", "SS", "IF", "OF", "P/1B", "P/OF", "C/OF", "3B/P"];

export default function PlayersSection() {
  const { items, loading, reload } = useResource("players");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);

  const sorted = [...items].sort((a, b) => (a.num ?? 99) - (b.num ?? 99));

  const save = async () => {
    if (!editing.name.trim()) {
      toast.error("Please enter the player's name.");
      return;
    }
    setSaving(true);
    const payload = { name: editing.name.trim(), num: Number(editing.num) || 0, pos: editing.pos, grade: Number(editing.grade), bt: editing.bt, photo: editing.photo || "" };
    try {
      if (editing.id) await api.put(`/admin/players/${editing.id}`, payload);
      else await api.post("/admin/players", payload);
      toast.success("Player saved! The roster on the website is updated.");
      setEditing(null);
      reload();
    } catch {
      toast.error("Could not save the player. Please try again.");
    }
    setSaving(false);
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/players/${deleting.id}`);
      toast.success("Player removed from the roster.");
      setDeleting(null);
      reload();
    } catch {
      toast.error("Could not delete the player.");
    }
  };

  if (loading) return <Loading />;

  return (
    <div data-testid="admin-players-section">
      <SectionHeader
        title="Players"
        subtitle="Add, edit, or remove players. The public roster updates as soon as you save."
        action={
          <PrimaryBtn onClick={() => setEditing({ ...EMPTY })} data-testid="add-player-button">
            <Plus size={17} /> Add New Player
          </PrimaryBtn>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="players-list">
        {sorted.length === 0 && <p className="col-span-full bg-white border border-slate-200 rounded-lg p-8 text-center text-slate-500">No players yet. Click "Add New Player".</p>}
        {sorted.map((p) => (
          <div key={p.id} className="bg-white rounded-lg border border-slate-200 p-4 flex items-center gap-4" data-testid={`player-row-${p.id}`}>
            {p.photo ? (
              <img src={p.photo} alt={p.name} className="h-14 w-14 rounded-full object-cover ring-2 ring-[#0F4C2A]/15" />
            ) : (
              <div className="h-14 w-14 rounded-full bg-[#0F4C2A] flex items-center justify-center shrink-0">
                <span className="font-stat font-bold text-[#F59E0B]">#{p.num}</span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-condensed text-lg font-bold uppercase text-slate-900 truncate">{p.name}</p>
              <p className="text-xs text-slate-500 font-semibold">#{p.num} · {p.pos} · {GRADES[p.grade] || p.grade} · B/T {p.bt}</p>
            </div>
            <button onClick={() => setEditing({ ...p })} className="p-2.5 text-[#0F4C2A] hover:bg-[#0F4C2A]/10 rounded-md" aria-label="Edit player" data-testid={`player-edit-${p.id}`}>
              <Pencil size={16} />
            </button>
            <button onClick={() => setDeleting(p)} className="p-2.5 text-red-700 hover:bg-red-50 rounded-md" aria-label="Delete player" data-testid={`player-delete-${p.id}`}>
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>

      {editing && (
        <Modal title={editing.id ? "Edit Player" : "Add New Player"} onClose={() => setEditing(null)} testid="player-modal">
          <div className="space-y-4">
            <Field label="Player Name">
              <input className={inputCls} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="First and last name" data-testid="player-form-name" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Jersey Number">
                <input type="number" min="0" max="99" className={inputCls} value={editing.num} onChange={(e) => setEditing({ ...editing, num: e.target.value })} data-testid="player-form-number" />
              </Field>
              <Field label="Position">
                <select className={inputCls} value={editing.pos} onChange={(e) => setEditing({ ...editing, pos: e.target.value })} data-testid="player-form-position">
                  {POSITIONS.map((p) => <option key={p}>{p}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Class / Year">
                <select className={inputCls} value={editing.grade} onChange={(e) => setEditing({ ...editing, grade: e.target.value })} data-testid="player-form-grade">
                  <option value={9}>Freshman (9)</option>
                  <option value={10}>Sophomore (10)</option>
                  <option value={11}>Junior (11)</option>
                  <option value={12}>Senior (12)</option>
                </select>
              </Field>
              <Field label="Bats / Throws">
                <select className={inputCls} value={editing.bt} onChange={(e) => setEditing({ ...editing, bt: e.target.value })} data-testid="player-form-bt">
                  {["R/R", "L/L", "R/L", "L/R", "S/R", "S/L"].map((b) => <option key={b}>{b}</option>)}
                </select>
              </Field>
            </div>
            <ImagePicker label="Player Photo (optional)" value={editing.photo} onChange={(url) => setEditing({ ...editing, photo: url })} testid="player-form-photo" />
            <div className="flex gap-3 pt-2">
              <PrimaryBtn className="flex-1" onClick={save} disabled={saving} data-testid="player-form-save">
                {saving ? "Saving..." : "Save Player"}
              </PrimaryBtn>
              <GhostBtn onClick={() => setEditing(null)} data-testid="player-form-cancel">Cancel</GhostBtn>
            </div>
          </div>
        </Modal>
      )}

      {deleting && (
        <ConfirmDialog
          message={`Remove ${deleting.name} from the roster? This cannot be undone.`}
          onConfirm={remove}
          onCancel={() => setDeleting(null)}
          testid="player-delete-confirm"
        />
      )}
    </div>
  );
}
